import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import jobTitleCoursesReportApi, {
  type JobTitleCoursesGroupI,
  type JobTitleCoursesKpisI,
  type JobTitleCoursesReportFiltersI,
} from 'Api/axios/job-title-courses-report-api';
import jobTitlesApi from 'Api/axios/job-titles-api';
import courseCategoriesApi from 'Api/axios/course-categories-api';
import useTranslation from 'Hooks/useTranslation';
import { downloadFile } from 'Utils/file-utils';
import type { PaginationMetaI } from 'types';
import type { MultiSelectOptionI } from '../components/MultiSelectFilter';
import type { JobTitleCoursesFilterValues } from '../components/JobTitleCoursesFilters';

const DEFAULT_PAGE_SIZE = 15;

const emptyFilters: JobTitleCoursesFilterValues = {
  search: '',
  jobTitleIds: [],
  categoryIds: [],
};

/**
 * Traduce el estado de la UI al contrato del endpoint, omitiendo lo que está
 * vacío para no mandar parámetros de más.
 */
const toApiFilters = (
  values: JobTitleCoursesFilterValues,
  page: number,
  limit: number
): JobTitleCoursesReportFiltersI => ({
  page,
  limit,
  search: values.search.trim() || undefined,
  jobTitleIds: values.jobTitleIds.length > 0 ? values.jobTitleIds : undefined,
  categoryIds: values.categoryIds.length > 0 ? values.categoryIds : undefined,
});

/**
 * Estado y efectos del Reporte de Cursos por Puesto.
 *
 * Los filtros viven aquí y la paginación es un estado aparte, así que cambiar
 * de página conserva los filtros; cambiar un filtro sí regresa a la página 1
 * (si no, el usuario podría quedar en una página que ya no existe).
 */
const useJobTitleCoursesReportPage = () => {
  const { t2 } = useTranslation('JobTitleCoursesReport');

  const [filters, setFilters] = useState<JobTitleCoursesFilterValues>(emptyFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [groups, setGroups] = useState<JobTitleCoursesGroupI[]>([]);
  const [kpis, setKpis] = useState<JobTitleCoursesKpisI | null>(null);
  const [pagination, setPagination] = useState<PaginationMetaI | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [jobTitleOptions, setJobTitleOptions] = useState<MultiSelectOptionI[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<MultiSelectOptionI[]>([]);

  // Catálogos de los filtros. Se cargan una sola vez; si fallan, el reporte
  // sigue siendo usable con el buscador de curso.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [jobTitles, categories] = await Promise.all([jobTitlesApi.readAll(), courseCategoriesApi.readAll()]);
      if (cancelled) return;

      const toOptions = (data: unknown): MultiSelectOptionI[] =>
        Array.isArray(data) ? (data as MultiSelectOptionI[]).map(({ id, name }) => ({ id, name })) : [];

      // Los puestos inactivos se descartan aquí, no en `/job-titles`: ese
      // endpoint lo comparten otras pantallas que sí necesitan verlos. Si se
      // ofrecieran como opción, elegir uno devolvería un reporte vacío y
      // parecería que el filtro está roto.
      const activeJobTitles = Array.isArray(jobTitles.data)
        ? (jobTitles.data as Array<{ status?: boolean }>).filter(jobTitle => jobTitle.status === true)
        : [];

      setJobTitleOptions(toOptions(activeJobTitles));
      setCategoryOptions(toOptions(categories.data));
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      const response = await jobTitleCoursesReportApi.fetchReport(toApiFilters(filters, page, pageSize));
      if (cancelled) return;

      if (response.error) {
        setError(response.error);
        setGroups([]);
        setKpis(null);
        setPagination(null);
      } else {
        setError(null);
        setGroups(response.data?.groups ?? []);
        setKpis(response.data?.kpis ?? null);
        setPagination(response.pagination ?? null);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [filters, page, pageSize]);

  const handleFiltersChange = useCallback((next: JobTitleCoursesFilterValues) => {
    setPage(1);
    setFilters(next);
  }, []);

  const handlePageChange = useCallback((next: number) => setPage(next), []);

  const handleRowsPerPageChange = useCallback((next: number) => {
    setPage(1);
    setPageSize(next);
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    // El export ignora la paginación a propósito: entrega todo lo filtrado.
    const response = await jobTitleCoursesReportApi.exportReport(toApiFilters(filters, 1, pageSize));
    setExporting(false);

    if (response.error) {
      toast.error(response.error);
      return;
    }
    if (response.data) {
      downloadFile(response.data, `cursos-por-puesto-${Date.now()}.xlsx`);
      toast.success(t2('toasts.exported') as string);
    }
  }, [filters, pageSize, t2]);

  return {
    t2,
    filters,
    groups,
    kpis,
    pagination,
    loading,
    error,
    exporting,
    jobTitleOptions,
    categoryOptions,
    handleFiltersChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
  };
};

export default useJobTitleCoursesReportPage;
